"use client";

import { useRef, useState } from "react";
import { getStorageItem, setStorageItem } from "~/lib/storage/Storage";

import VolumeIcon from "@/icons/ui/volume.svg";
import MuteIcon from "@/icons/ui/mute.svg";
import RestartIcon from "@/icons/ui/restart.svg";
import { useClientMediaQuery } from "~/hooks/useClientMediaQuery";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import VideoPlayer from "~/componentsDS/VideoPlayer";

const actionButtonClassNames = "flex items-center gap-2 bg-white/10 backdrop-blur-[40px] text-[12px] text-pureWhite font-suisseBP font-medium uppercase tracking-wide leading-[16px] py-2 px-2 lg:px-4 rounded-3xl";

const VSL_META = {
    vsl_id: "ib_patient_reviews_vsl",
};

const VideoReview = () => {
    const [isVideoMuted, setVideoMuted] = useState(true);
    const isMobile = useClientMediaQuery("(max-width: 767px)");

    const sendEvent = useTrackEvent();

    const videoRef = useRef<HTMLVideoElement>(null);
    const hasEndedRef = useRef(false);

    const videoSrc = isMobile
        ? {
            mp4: "https://res.cloudinary.com/dirscuuhp/video/upload/v1769797289/new_design/Branding-Video_Final-Mobile_withbrandlogo_vxsg0a.mp4",
            hsl: "https://res.cloudinary.com/dirscuuhp/video/upload/v1769797289/new_design/Branding-Video_Final-Mobile_withbrandlogo_vxsg0a.m3u8",
        }
        : {
            mp4: "https://res.cloudinary.com/dirscuuhp/video/upload/v1762252336/new_design/Branding-Video_Final_Captiosn_withupdatedbrand_aej1vc.mp4",
            hsl: "https://res.cloudinary.com/dirscuuhp/video/upload/v1762252336/new_design/Branding-Video_Final_Captiosn_withupdatedbrand_aej1vc.m3u8",
        };

    const getVideoMeta = () => {
        const video = videoRef.current;
        if (!video) return null;

        return {
            vsl_id: VSL_META.vsl_id,
            vsl_duration: video.duration,
            vsl_current_time: video.currentTime,
            vsl_completed: hasEndedRef.current,
            vsl_sound: !video.muted,
        };
    };

    const trackEvent = (event_name: "vsl_sound" | "vsl_restart") => {
        const meta = getVideoMeta();
        if (!meta) return;

        sendEvent({
            event: "user_interaction",
            value: {
                event_name,
                widget: "ib_patient_reviews",
                meta_data: meta,
            },
        });
    };

    const toggleSound = () => {
        const video = videoRef.current;
        if (!video) return;

        const nextMuted = !isVideoMuted;
        setVideoMuted(nextMuted);

        // ensure DOM state is in sync
        video.muted = nextMuted;

        trackEvent("vsl_sound");
    };

    const restartVideo = () => {
        const video = videoRef.current;
        if (!video) return;

        hasEndedRef.current = false;
        video.currentTime = 0;
        video.play();

        trackEvent("vsl_restart");
    };

    const handleLoadedData = () => {
        const video = videoRef.current;
        if (!video) return;

        const storedTime = getStorageItem("VSL_Current_Time");
        video.currentTime = storedTime ? Number(storedTime) : 0;
    };

    const handleTimeUpdate = () => {
        const video = videoRef.current;
        if (!video) return;

        setStorageItem("VSL_Current_Time", video.currentTime.toString());
    };

    const handleEnded = () => {
        hasEndedRef.current = true;
        setStorageItem("VSL_Video_Ended", "true");
    };

    return (
        <div className="relative group">
            <div className="relative w-full min-h-[515px] md:min-h-[344px] xl:min-h-[610px] aspect-[352/515] md:aspect-[672/344] xl:aspect-[1190/610] overflow-hidden rounded-2xl bg-heroBlue">
                <VideoPlayer
                    key={videoSrc.mp4}
                    ref={videoRef}
                    mp4={videoSrc.mp4}
                    hls={videoSrc.hsl}
                    playsInline
                    autoPlay
                    loop
                    muted={isVideoMuted}
                    preload="metadata"
                    className="absolute inset-0 w-full h-full object-cover"
                    poster="https://res.cloudinary.com/dirscuuhp/image/upload/v1769611560/new_design/Screenshot_2025-10-15_at_3.26.44_PM_1_mh3yv1.webp"
                    onLoadedData={handleLoadedData}
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={handleEnded}
                    onClick={toggleSound}
                />
            </div>

            <div className="absolute z-20 right-4 top-4 lg:right-6 lg:top-6 flex gap-3">
                <button
                    className={actionButtonClassNames}
                    onClick={toggleSound}
                >
                    <span className="max-lg:hidden pt-[2px]">{isVideoMuted ? "Click for Sound" : "Click to Mute"}</span>
                    {isVideoMuted ? <VolumeIcon width="16" height="16" fill="#fff"/> : <MuteIcon width="16" height="16" fill="#fff"/>}
                </button>

                <button
                    className={actionButtonClassNames}
                    onClick={restartVideo}
                >
                    <span className="max-lg:hidden pt-[2px]">Restart Video</span>
                    <RestartIcon width="16" height="16" fill="#fff"/>
                </button>
            </div>
        </div>
    );
};

export default VideoReview;
